import { FeesRepo } from "../fees.repo";
import { ClientRepo } from "../../clients/client.repo";
import { FeeType } from "../models/fee.models";
import * as _ from "lodash";
import * as moment from "moment";
import { AuthService } from "../../common/auth.service";
import { PortfolioRepo } from "../../portfolio/portfolio.repo";
import { Roles } from "../../common/enums/Roles";

class ClientFeeManagementController implements ng.IOnInit {
  public clients;
  public selectedClient;
  public searchText: string;
  public clientFees;
  public feeTypes: FeeType[];
  public clientDetails;
  public isLoadingClientDetails: boolean = false;
  public isLoadingClientFee: boolean = false;
  public clientFeeExclusions: Array<any>;
  public clientCashAccountsGroupedByCurrency: Array<any>;
  public clientCashAccounts: Array<any>;
  public clientFeeCashAccounts: Array<any>;
  public potentialPortfoliosForFees: any;

  /** @ngInject */
  constructor(
    private feesRepo: FeesRepo,
    private clientRepo: ClientRepo,
    private notifyjs,
    private $mdDialog: angular.material.IDialogService,
    private $rootScope: angular.IRootScopeService,
    private authService: AuthService,
    private portfolioRepo: PortfolioRepo
  ) {}

  $onInit = () => {
    this.getClients();
    this.getFeeTypes();
    this.getClientFeeExclusions();
  };

  getClients = () => {
    this.isLoadingClientDetails = true;
    this.clientRepo
      .getClients(
        null,
        null,
        null,
        null,
        "clientNumber",
        "asc",
        true,
        null,
        null
      )
      .then((res) => {
        let data = res.data as any;
        this.clients = data.results as Array<any>;
        this.isLoadingClientDetails = false;
      })
      .catch((err) => {
        this.notifyjs("Failed to load client details");
        this.isLoadingClientDetails = false;
      });
  };

  removePortfolioLink = (clientFee, portfolio) => {
    _.remove(clientFee.portfolios, (p) => {
      return p["portfolioId"] === portfolio.portfolioId;
    });

    this.potentialPortfoliosForFees.push(portfolio);
  };

  updatePotentialPortfoliosList = (clientFee) => {
    let listOfPortfoliosLinked = clientFee.portfolios.map((portfolio) => {
      return portfolio.portfolioId;
    });

    _.remove(this.potentialPortfoliosForFees, (p) => {
      return _.indexOf(listOfPortfoliosLinked, p["portfolioId"]) > -1;
    });
  };

  getPortfolioAccountNumber = (portfolio) => {
    if (portfolio) {
      return portfolio.name;
    }
  };

  getAvailablePortfolios = (clientFee) => {
    if (clientFee) {
      this.updatePotentialPortfoliosList(clientFee);
      return this.potentialPortfoliosForFees;
    }
    return [];
  };

  getClientFees = () => {
    if (this.selectedClient) {
      this.getClient();
      this.isLoadingClientFee = true;
      this.feesRepo
        .getClientFees(this.selectedClient.clientNumber)
        .then((res) => {
          this.clientFees = res.data;
          this.isLoadingClientFee = false;
        })
        .catch((err) => {
          this.notifyjs(`Failed to load client fees : ${err.data}`);
          this.isLoadingClientFee = false;
        });
    }
  };

  clearSearchText = () => {
    this.searchText = "";
  };

  convertDecToPerc = (x: number) => {
    return +(x * 100).toFixed(4);
  };

  formatBooleanValue = (value: boolean) => {
    return value ? "Yes" : "No";
  };

  convertToLocaleTime = (date: any) => {
    date = new Date(date);
    return moment(date).format("DD-MM-YYYY");
  };

  getFeeTypes = () => {
    this.feesRepo
      .getFeeTypes(true)
      .then((res) => {
        this.feeTypes = res.data as Array<FeeType>;
      })
      .catch((err) => {
        this.notifyjs("Failed to load fee types");
      });
  };

  editPortfolioFee = (portfolioFee) => {
    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.portfolioFee = portfolioFee;
    tempScope.feeTypes = this.feeTypes;
    tempScope.clientPortfolios = this.clientDetails.portfolios;
    tempScope.potentialPortfoliosForFees = this.clientDetails.portfolios;

    this.$mdDialog
      .show({
        template: `<create-portfolio-fee-dialog portfolio-fee="portfolioFee" fee-types="feeTypes"
                  client-portfolios = "clientPortfolios" potential-portfolios-for-fees = "potentialPortfoliosForFees">
                  </create-portfolio-fee-dialog>`,
        clickOutsideToClose: true,
        scope: tempScope,
      })
      .then((result) => {
        if (result) {
          this.getClientFees();
        }
      })
      .catch(() => {
        this.getClientFees();
      });
  };

  addFee = () => {
    this.editPortfolioFee(null);
  };

  getClient = () => {
    this.clientRepo.getClient(this.selectedClient.clientNumber).then((res) => {
      this.clientDetails = res.data;
      this.potentialPortfoliosForFees = this.clientDetails.portfolios;
      this.getClientFeeExclusions();
      this.getClientCashAccounts();
      this.getDefaultFeeCashAccounts(this.clientDetails.id);
    });
  };

  hasClientFee = () => {
    return (
      this.selectedClient &&
      !_.isUndefined(this.clientFees) &&
      this.clientFees.length
    );
  };

  editClientFee = (clientFee) => {
    if (clientFee.toDate) {
      const actualDate = new Date(
        Date.UTC(
          clientFee.toDate.getFullYear(),
          clientFee.toDate.getMonth(),
          clientFee.toDate.getDate()
        )
      );
      clientFee.toDate = actualDate;
    }
    this.feesRepo
      .editClientFee(clientFee)
      .then((res) => {
        this.getClientFees();
      })
      .catch((err) => {
        this.notifyjs(`Failed to update fee : ${err.data}`);
      });
  };

  cancelClientFee = () => {
    this.getClientFees();
  };

  getClientFeeExclusions = () => {
    this.clientFeeExclusions = [];
    if (this.clientDetails) {
      this.clientDetails.portfolios.forEach((portfolio) => {
        this.feesRepo
          .getPortfolioFeeExclusion(portfolio.portfolioId)
          .then((res) => {
            this.clientFeeExclusions.push(res.data);
          })
          .catch((err) => {
            this.notifyjs("Failed to load Fee Exclusions " + err.data);
          });
      });
    }
  };

  addFeeExclusion = (portfolio) => {
    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.portfolioId = portfolio.portfolioId;
    tempScope.portfolioName = portfolio.portfolioName;

    this.$mdDialog
      .show({
        template: `<create-portfolio-fee-exclusion-dialog portfolio-id="portfolioId" portfolio-name = "portfolioName"></create-portfolio-fee-exclusion-dialog>`,
        clickOutsideToClose: true,
        scope: tempScope,
      })
      .then((result) => {
        if (result) {
          this.getPortfolioFeeExclusions(portfolio.portfolioId);
        }
      })
      .catch(() => {
        this.getPortfolioFeeExclusions(portfolio.portfolioId);
      });
  };

  getPortfolioFeeExclusions = (portfolioId) => {
    this.feesRepo
      .getPortfolioFeeExclusion(portfolioId)
      .then((res) => {
        let existingPortfolioFeeExclusionIndex =
          this.clientFeeExclusions.findIndex((f) => {
            return f.portfolioId == portfolioId;
          });
        if (existingPortfolioFeeExclusionIndex > -1) {
          this.clientFeeExclusions[existingPortfolioFeeExclusionIndex] =
            res.data;
        }
      })
      .catch((err) => {
        this.notifyjs("Failed to load fee exclusion : " + err.data);
      });
  };

  getBoolYesNo = (booleanVal: boolean) => {
    return booleanVal ? "Yes" : "No";
  };

  editPortfolioFeeExclusion = (portfolioFeeExclusion) => {
    const actualtoDate =
      portfolioFeeExclusion.toDate != null
        ? new Date(
            Date.UTC(
              portfolioFeeExclusion.toDate.getFullYear(),
              portfolioFeeExclusion.toDate.getMonth(),
              portfolioFeeExclusion.toDate.getDate()
            )
          )
        : null;
    portfolioFeeExclusion.toDate = actualtoDate;
    this.feesRepo
      .editPortfolioFeeExclusion(portfolioFeeExclusion)
      .then((res) => {
        this.getPortfolioFeeExclusions(portfolioFeeExclusion.portfolioId);
      })
      .catch((err) => {
        this.getPortfolioFeeExclusions(portfolioFeeExclusion.portfolioId);
      });
  };

  cancelPortfolioFeeExclusion = (portfolioFeeExclusion) => {
    this.getPortfolioFeeExclusions(portfolioFeeExclusion.portfolioId);
  };

  getTitle = (portfolioFeeExclusion) => {
    return portfolioFeeExclusion.modifiedBy
      ? `Modified on ${new Date(portfolioFeeExclusion.dateModified)
          .toISOString()
          .substr(0, 10)} by ${portfolioFeeExclusion.modifiedBy}`
      : "";
  };

  isSeniorManager = () => {
    return (
      this.authService.getUserRoles().indexOf(Roles.Accountant) > -1 ||
      this.authService.getUserRoles().indexOf(Roles.SystemAdministrator) > -1
    );
  };

  getClientCashAccounts = () => {
    let portfolioIds = this.clientDetails.portfolios.map(
      (portfolio) => portfolio.portfolioId
    );
    this.portfolioRepo
      .getPortfolioCashAccounts(portfolioIds)
      .then((res) => {
        this.clientCashAccounts = res.data as Array<any>;
        this.clientCashAccountsGroupedByCurrency = _.chain(
          this.clientCashAccounts
        )
          .groupBy("currency")
          .toPairs()
          .map(function (currentItem) {
            return _.zipObject(["currency", "items"], currentItem);
          })
          .value();
      })
      .catch((err) => {});
  };

  getDefaultFeeCashAccounts = (clientId) => {
    this.feesRepo
      .getClientFeeCashAccounts(clientId)
      .then((res) => {
        this.clientFeeCashAccounts = res.data as Array<any>;
      })
      .catch((err) => {});
  };

  cancelPortfolioFeeCashAccount = () => {
    this.getDefaultFeeCashAccounts(this.clientDetails.id);
  };

  editPortfolioFeeCashAccount = (account) => {
    this.feesRepo
      .addEditPortfolioFeeCashAccount(account)
      .then((res) => {
        this.getDefaultFeeCashAccounts(this.clientDetails.id);
      })
      .catch((err) => {
        this.notifyjs("Failed to add cash account : ", err.data);
      });
  };

  selectCashAccountCurrency = (account) => {
    let selectedAccount = this.clientCashAccounts.filter((cashAccount) => {
      return cashAccount.id == account.accountId;
    });
    account.currency = _.first(selectedAccount).currency;
  };
}

export const clientFeeManagement: ng.IComponentOptions = {
  template: require("./fees.clientFeeManagement.html"),
  controller: ClientFeeManagementController,
};



// WEBPACK FOOTER //
// ./src/app/fees/clientFeeManagement/fees.clientFeeManagement.ts